/* eslint-disable local/no-conditional-class-names */
import { Card, CardBody, Code, Link, MenuItem, Text } from '@apollo/orbit';
import React, { useEffect, useRef } from 'react';

import { ampli } from 'src/lib/analytics/amplitude/vendor';
import { DocsRouter } from 'src/lib/routers';

export const RoverInitMenuItem = () => {
  const copyButtonRef = useRef<HTMLButtonElement | null>(null);
  const linkRef = useRef<HTMLAnchorElement | null>(null);
  const roverDocsUrl = DocsRouter.path('Rover');

  const menuItemClassName = `
  !cursor-default 
  hover:!bg-transparent 
  focus:!bg-transparent 
  focus-visible:!shadow-none 
  focus-visible:!outline-none
  focus-visible:!ring-0
  active:!bg-transparent
  active:!font-normal
  active:!text-inherit
`;

  // Focus handlers for keyboard navigation
  const handleCodeMenuItemFocus = () => {
    if (copyButtonRef.current) {
      copyButtonRef.current.focus();
    }
  };

  const handleLinkMenuItemFocus = () => {
    if (linkRef.current) {
      linkRef.current.focus();
    }
  };

  // We need a direct keyboard handler for handling keyboard interactions with the link
  useEffect(() => {
    const handleLinkKeyDown = (e: KeyboardEvent) => {
      const linkMenuItem = linkRef.current;
      const isTargetWithinLinkItem =
        linkMenuItem &&
        (linkMenuItem === e.target || linkMenuItem.contains(e.target as Node));

      if (isTargetWithinLinkItem && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        e.stopPropagation();

        ampli.roverInstallationMethodsClicked();

        window.open(roverDocsUrl, '_blank', 'noopener,noreferrer');

        return false;
      }
    };

    // We have to use capture phase to intercept events early
    document.addEventListener('keydown', handleLinkKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleLinkKeyDown, true);
    };
  }, [roverDocsUrl]);

  return (
    <>
      <MenuItem
        key="rover-init-code"
        as="div"
        onFocus={handleCodeMenuItemFocus}
        className={menuItemClassName}
      >
        <Card variant="unstyled">
          <CardBody>
            <Text>
              Create a new graph by running this command in an empty directory:
            </Text>
            <Code
              buttonRef={copyButtonRef}
              canCopy
              onClick={(e) => {
                e.stopPropagation();
                ampli.cliCommandCopied({
                  CommandType: 'Onboarding',
                  CommandContent: 'rover init',
                  Location: 'ZeroState',
                });
              }}
              className="h-12 w-full justify-between p-3"
            >
              rover init
            </Code>
          </CardBody>
        </Card>
      </MenuItem>

      <MenuItem
        key="rover-init-link"
        as="div"
        data-testid="rover-init-link-item"
        onFocus={handleLinkMenuItemFocus}
        className={menuItemClassName}
      >
        <Link
          ref={linkRef}
          isExternal={true}
          className="text-sm"
          to={roverDocsUrl}
        >
          Learn more about Rover.
        </Link>
      </MenuItem>
    </>
  );
};
